import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { TApiSupervisorContragentBlock } from '~/modules/covenants/types.ts';
import type {
  TSupervisorContragentBlackListFormValues,
} from '~/modules/covenants/CovenantsEmailBlackList/types.ts';
import { getCovenantsRequestErrorMessage } from '~/modules/covenants/utils.ts';
import { removeSupervisorContragentBlock } from '~/store/covenants/slice.ts';
import {
  createSupervisorContragentBlock,
  deleteSupervisorContragentBlock,
  fetchSupervisorContragentBlocks,
} from '~/store/covenants/thunks/contragentThunks.ts';
import type { AppDispatch } from '~/store/store.ts';

type TUseSupervisorContragentBlackListActionsParams = {
  dispatch: AppDispatch;
  contragentId?: number;
  blocks: TApiSupervisorContragentBlock[];
  page: number;
  rowsPerPage: number;
  setPage: Dispatch<SetStateAction<number>>;
  onError: (message: string) => void;
};

const useSupervisorContragentBlackListActions = ({
  dispatch,
  contragentId,
  blocks,
  page,
  rowsPerPage,
  setPage,
  onError,
}: TUseSupervisorContragentBlackListActionsParams) => {
  const [isSubmittingAdd, setIsSubmittingAdd] = useState(false);
  const [deletingEmail, setDeletingEmail] = useState<string | null>(null);

  const refreshBlocks = async (nextPage = page, nextRowsPerPage = rowsPerPage) => {
    if (!contragentId) {
      return;
    }

    await dispatch(
      fetchSupervisorContragentBlocks({
        contragentId,
        page: nextPage,
        size: nextRowsPerPage,
      })
    ).unwrap();
  };

  const handleAddEmail = async (values: TSupervisorContragentBlackListFormValues) => {
    if (!contragentId) {
      return;
    }

    setIsSubmittingAdd(true);

    try {
      await dispatch(
        createSupervisorContragentBlock({
          contragentId,
          email: values.email.trim(),
        })
      ).unwrap();
      await refreshBlocks();
    } catch (error) {
      onError(getCovenantsRequestErrorMessage(error));
      throw error;
    } finally {
      setIsSubmittingAdd(false);
    }
  };

  const handleDeleteEmail = async (email: string) => {
    if (!contragentId) {
      return;
    }

    setDeletingEmail(email);

    try {
      await dispatch(
        deleteSupervisorContragentBlock({
          contragentId,
          email,
        })
      ).unwrap();

      const shouldLoadPreviousPage = blocks.length === 1 && page > 0;
      dispatch(removeSupervisorContragentBlock({ email }));

      if (shouldLoadPreviousPage) {
        setPage((prevPage) => prevPage - 1);
      }
    } catch (error) {
      onError(getCovenantsRequestErrorMessage(error));
    } finally {
      setDeletingEmail((currentEmail) => (currentEmail === email ? null : currentEmail));
    }
  };

  return {
    isSubmittingAdd,
    deletingEmail,
    handleAddEmail,
    handleDeleteEmail,
  };
};

export default useSupervisorContragentBlackListActions;
